/**
 * Single parser/builder for `/emails` routes. Do not add a fourth
 * ungrouped encoding — convert at the adapter (list display `'Ungrouped'`,
 * Convex `__ungrouped__`, URL `ungrouped`).
 */
import { UNGROUPED_KEY } from '@/lib/canvas/group-order'

const EMAILS_LIST_SEGMENT = 'list'
export const EMAILS_UNGROUPED_SEGMENT = 'ungrouped'
export const EMAILS_RESERVED_SEGMENTS = [
  EMAILS_LIST_SEGMENT,
  EMAILS_UNGROUPED_SEGMENT,
] as const

const PRESERVED_SEARCH_KEYS = [
  'chat',
  'sendId',
  'emailIds',
  'focusEmailIds',
  'focusMode',
  'commentId',
] as const

export type EmailsRoute =
  | { kind: 'grid' }
  | { kind: 'list' }
  | { kind: 'ungrouped' }
  | { kind: 'group'; groupId: string }
  | { kind: 'legacy-email'; emailId: string }

function normalizePathname(pathname: string): string {
  const withoutHash = pathname.split('#')[0] ?? ''
  const withoutSearch = withoutHash.split('?')[0] ?? ''
  if (withoutSearch.length > 1 && withoutSearch.endsWith('/')) {
    return withoutSearch.slice(0, -1)
  }
  return withoutSearch
}

function isGroupIdSegment(segment: string): boolean {
  return segment.startsWith('grp_')
}

export function parseEmailsPath(
  pathname: string | null | undefined
): EmailsRoute {
  const path = normalizePathname(pathname ?? '')
  if (path === '' || path === '/emails') {
    return { kind: 'grid' }
  }
  if (!path.startsWith('/emails/')) {
    return { kind: 'grid' }
  }
  const segment = path.slice('/emails/'.length)
  if (segment.includes('/')) {
    return { kind: 'grid' }
  }
  if (segment === EMAILS_LIST_SEGMENT) {
    return { kind: 'list' }
  }
  if (segment === EMAILS_UNGROUPED_SEGMENT) {
    return { kind: 'ungrouped' }
  }
  if (isGroupIdSegment(segment)) {
    return { kind: 'group', groupId: segment }
  }
  return { kind: 'legacy-email', emailId: segment }
}

export function buildEmailsPath(
  route: Exclude<EmailsRoute, { kind: 'legacy-email' }>
): string {
  if (route.kind === 'grid') {
    return '/emails'
  }
  if (route.kind === 'list') {
    return '/emails/list'
  }
  if (route.kind === 'ungrouped') {
    return '/emails/ungrouped'
  }
  return `/emails/${route.groupId}`
}

export function convexGroupKeyToSegment(groupKey: string): string {
  if (
    groupKey === UNGROUPED_KEY ||
    groupKey === EMAILS_UNGROUPED_SEGMENT ||
    groupKey === 'Ungrouped'
  ) {
    return EMAILS_UNGROUPED_SEGMENT
  }
  return groupKey
}

export function segmentToConvexGroupKey(segment: string): string {
  if (
    segment === EMAILS_UNGROUPED_SEGMENT ||
    segment === UNGROUPED_KEY ||
    segment === 'Ungrouped'
  ) {
    return UNGROUPED_KEY
  }
  return segment
}

export function groupSegmentFromListGroupId(
  groupId: string | undefined
): string {
  return convexGroupKeyToSegment(groupId ?? UNGROUPED_KEY)
}

export function buildEmailsGroupPath(groupIdOrKey: string): string {
  const segment = convexGroupKeyToSegment(groupIdOrKey)
  if (segment === EMAILS_UNGROUPED_SEGMENT) {
    return buildEmailsPath({ kind: 'ungrouped' })
  }
  return buildEmailsPath({ kind: 'group', groupId: segment })
}

export function isEmailsGroupCanvasPath(
  pathname: string | null | undefined
): boolean {
  const route = parseEmailsPath(pathname)
  return route.kind === 'group' || route.kind === 'ungrouped'
}

export function isEmailsGridPath(pathname: string | null | undefined): boolean {
  return normalizePathname(pathname ?? '') === '/emails'
}

export function isEmailsSurfacePath(
  pathname: string | null | undefined
): boolean {
  const path = normalizePathname(pathname ?? '')
  return path === '/emails' || path.startsWith('/emails/')
}

/**
 * Groups | List header pills. Must not use `parseEmailsPath` alone —
 * that classifier returns `{ kind: 'grid' }` for non-emails URLs.
 */
export function shouldShowEmailsModeTabs(
  pathname: string | null | undefined
): boolean {
  return isEmailsSurfacePath(pathname) && !isEmailsGroupCanvasPath(pathname)
}

export function parseOptionalTargetGroupId(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return
  }
  const trimmed = value.trim()
  if (trimmed.length === 0 || trimmed.length > 64) {
    return
  }
  return trimmed
}

export function firstCsvValue(
  raw: string | null | undefined
): string | undefined {
  if (!raw) {
    return
  }
  const first = raw.split(',')[0]?.trim()
  return first && first.length > 0 ? first : undefined
}

function pickPreservedEmailsSearch(search: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams()
  for (const key of PRESERVED_SEARCH_KEYS) {
    const value = search.get(key)
    if (value) {
      next.set(key, value)
    }
  }
  return next
}

export function searchRecordToParams(
  record: Record<string, string | Array<string> | undefined>
): URLSearchParams {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string' && value.length > 0) {
      search.set(key, value)
    } else if (Array.isArray(value) && typeof value[0] === 'string') {
      search.set(key, value[0])
    }
  }
  return search
}

function hrefWithSearch(pathname: string, search: URLSearchParams): string {
  const query = search.toString()
  return query.length > 0 ? `${pathname}?${query}` : pathname
}

/**
 * Grid (`/emails`) redirects. `chat` / `sendId` / focus params survive;
 * `groupIds` is dropped (the path is the group).
 *
 * - `emailIds` / `focusEmailIds` / `commentId` → first id's group, or
 *   `ungrouped` when unknown / pre-minted.
 * - Single `groupIds` → that group; multi → stay on the grid (`null`).
 */
export function buildGridRedirectHref(args: {
  search: URLSearchParams
  focusGroupSegment?: string | null
}): string | null {
  const emailIds = args.search.get('emailIds')
  const focusEmailIds = args.search.get('focusEmailIds')
  const commentId = args.search.get('commentId')
  if (emailIds || focusEmailIds || commentId) {
    const segment = args.focusGroupSegment ?? EMAILS_UNGROUPED_SEGMENT
    return hrefWithSearch(
      buildEmailsGroupPath(segment),
      pickPreservedEmailsSearch(args.search)
    )
  }
  const groupIds = args.search.get('groupIds')
  if (!groupIds) {
    return null
  }
  const ids = groupIds
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id.length > 0)
  if (ids.length !== 1 || !ids[0]) {
    return null
  }
  return hrefWithSearch(
    buildEmailsGroupPath(ids[0]),
    pickPreservedEmailsSearch(args.search)
  )
}

/**
 * `/emails/<segment>` that is not `list` / `ungrouped` / `grp_*` is a
 * legacy email id. Unknown → `/emails`. Known → that email's group canvas.
 */
export function buildLegacySegmentRedirectHref(args: {
  segment: string
  search: URLSearchParams
  resolvedGroupSegment: string | null
}): string | null {
  const route = parseEmailsPath(`/emails/${args.segment}`)
  if (
    route.kind === 'group' ||
    route.kind === 'ungrouped' ||
    route.kind === 'list' ||
    route.kind === 'grid'
  ) {
    return null
  }
  if (args.resolvedGroupSegment === null) {
    const clean = new URLSearchParams()
    const chat = args.search.get('chat')
    const sendId = args.search.get('sendId')
    if (chat) {
      clean.set('chat', chat)
    }
    if (sendId) {
      clean.set('sendId', sendId)
    }
    return hrefWithSearch('/emails', clean)
  }
  const preserved = pickPreservedEmailsSearch(args.search)
  if (!preserved.get('emailIds')) {
    preserved.set('emailIds', args.segment)
    preserved.set('focusEmailIds', args.segment)
  }
  return hrefWithSearch(
    buildEmailsGroupPath(args.resolvedGroupSegment),
    preserved
  )
}
